/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import React, { PureComponent } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, DomEventListener, Mixins, Text, TextInput } from '@blend/enterprise-components';
import { keyCodes } from '@blend/shared-react';
import { getSafeId } from './utils';
import { TypeaheadMultiSelectSuggestion } from './TypeaheadMultiSelectSuggestion';
import { DismissableTileList } from '@components/DismissableTileList/DismissableTileList';
import { diagnostics } from '@utils/diagnostics';
import { debounce, isEmpty, sortBy, uniqBy, includes, inRange, find, cloneDeep, map } from '@utils/lodash';

const styles = StyleSheet.create({
  noResults: {
    backgroundColor: BrandColors.NEUTRAL_25,
    paddingLeft: 12,
    height: 36,
  },
  suggestionsContainer: {
    ...Mixins.borderBottom(BrandColors.NEUTRAL_150, 1),
    ...Mixins.border(BrandColors.NEUTRAL_150, 1, 'solid', 'borderLeft'),
    ...Mixins.border(BrandColors.NEUTRAL_150, 1, 'solid', 'borderRight'),
    backgroundColor: BrandColors.WHITE,
    borderRadius: 3,
    maxHeight: 300,
    overflow: 'auto',
    paddingInlineStart: 0,
    position: 'absolute',
    width: '100%',
    zIndex: 1,
  },
  suggestionWrapper: {
    ...Mixins.borderBottom('transparent', 1),
    ...Mixins.border('transparent', 1, 'solid', 'borderTop'),
    ...Mixins.paddingVertical(5),
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    paddingLeft: 5,
    ':hover': {
      backgroundColor: BrandColors.NEUTRAL_25,
    },
  },
  showSuggestions: {
    display: 'flex',
  },
  hideSuggestions: {
    display: 'none',
  },
  suggestionsRoot: {
    position: 'relative',
  },
});

export namespace TypeaheadMultiSelect {
  export interface Selections {
    [id: string]: Suggestion;
  }

  export interface Suggestion {
    display: string;
    value: string;
    exclusive?: boolean;
  }
  export interface Props {
    id: string;
    selections: Selections;
    getNewSuggestions: (
      value: string,
    ) => Promise<TypeaheadMultiSelect.Suggestion[]> | TypeaheadMultiSelect.Suggestion[];
    onSelectionChange: (selections: Selections) => void;
    getPlaceholderText: (selections: Selections) => string;
    renderFooter?: (state: TypeaheadMultiSelect.State) => React.ReactNode;
    showDismissableTiles?: boolean;
    ariaLabelledById?: string;
    disabled?: boolean;
  }
  export interface State {
    value: string;
    suggestionsAreOpen: boolean;
    suggestions: TypeaheadMultiSelect.Suggestion[];
    activeSuggestionIndex: number;
  }
}

export class TypeaheadMultiSelect extends PureComponent<TypeaheadMultiSelect.Props, TypeaheadMultiSelect.State> {
  private rootNode: Element | null = null;
  private textInputNode: HTMLInputElement | null = null;

  private debouncedAddNewSuggestions: (value: string) => Promise<void> | undefined;
  constructor(props: TypeaheadMultiSelect.Props) {
    super(props);
    this.state = {
      value: '',
      suggestions: [],
      suggestionsAreOpen: false,
      activeSuggestionIndex: -1,
    };
    this.debouncedAddNewSuggestions = debounce(this.addNewSuggestions, 200);
  }

  async componentDidMount() {
    await this.addNewSuggestions('');
  }

  addNewSuggestions = async (value: string) => {
    const { getNewSuggestions } = this.props;
    try {
      const newSuggestions = await getNewSuggestions(value);
      this.handleNewSuggestions(newSuggestions);
    } catch (err) {
      diagnostics.error(new Error('Unable to retrieve typeahead suggestions'));
    }
  };

  handleNewSuggestions(newSuggestions: TypeaheadMultiSelect.Suggestion[]) {
    const { value } = this.state;
    const { id, selections } = this.props;
    this.setState({ activeSuggestionIndex: -1 });
    if (isEmpty(newSuggestions)) {
      return this.setState({ suggestions: newSuggestions });
    }
    const exclusiveSuggestion = newSuggestions.filter(suggestion => !!suggestion.exclusive);
    if (exclusiveSuggestion.length > 1) {
      const error = new Error(`Error with component ${id}: More than one exclusive suggestion not supported`);
      diagnostics.error(error);
      throw error;
    }
    // empty text input means we should include all selections in dropdown
    if (value === '') {
      // currently this sort only supports alphabetical, if an alternative
      // sort is needed pass it in as a prop
      const selectionsArray = sortBy(Object.values(selections), 'display');
      // could be dups between newSuggestions and selectionsArray
      this.setState({
        suggestions: uniqBy(
          [...exclusiveSuggestion, ...selectionsArray, ...newSuggestions],
          selection => selection.value,
        ),
      });
    } else {
      this.setState({
        suggestions: uniqBy([...exclusiveSuggestion, ...newSuggestions], selection => selection.value),
      });
    }
  }

  closeSuggestions = () => {
    this.setState({ suggestionsAreOpen: false });
    /* istanbul ignore next */
    if (this.textInputNode) {
      // want to remove focus from input element when closing since we fill the input
      // with the current selections
      /* istanbul ignore next */
      this.textInputNode.blur();
    }
  };

  /* istanbul ignore next */
  closeOnOutsideClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const { suggestionsAreOpen } = this.state;
    if (this.rootNode && this.rootNode.contains(event.target as Node)) {
      // the click is inside the component - do nothing
      return;
    }
    // we want to close the open component on any outside clicks
    if (suggestionsAreOpen) {
      this.closeSuggestions();
    }
  };

  // dropdown should close on tab or enter key
  maybeCloseOnKeyPress = ({ key }: KeyboardEvent) => {
    const { suggestionsAreOpen } = this.state;
    if ((key === 'Tab' || key === 'Enter' || key === 'Escape') && suggestionsAreOpen) {
      this.closeSuggestions();
    }
  };

  handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { suggestions, activeSuggestionIndex } = this.state;
    const { UP_ARROW, DOWN_ARROW, SPACE } = keyCodes;
    const KEY = e.keyCode;

    const numSuggestions = suggestions.length;
    if (includes([DOWN_ARROW, UP_ARROW], KEY)) {
      e.preventDefault();
      // Navigate up or down
      const newActiveIndex = KEY === DOWN_ARROW ? activeSuggestionIndex + 1 : activeSuggestionIndex - 1;
      const canMoveToNewActiveIndex = inRange(newActiveIndex, 0, numSuggestions) && numSuggestions > 0;

      if (canMoveToNewActiveIndex) {
        this.setState({ activeSuggestionIndex: newActiveIndex, value: suggestions[newActiveIndex].display });
      }
    } else if (KEY === SPACE) {
      // Check that the index is on a valid row first
      if (inRange(activeSuggestionIndex, 0, numSuggestions) && numSuggestions > 0) {
        e.preventDefault();
        // Select or deselect the suggestion
        this.onSelectSuggestion(suggestions[activeSuggestionIndex]);
      }
    }
  };

  onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.setState({ value: newValue }, async () => {
      await this.debouncedAddNewSuggestions(newValue);
    });
  };

  exclusiveSuggestionIsSelected() {
    const { selections } = this.props;
    return !!find(selections, selection => !!selection.exclusive);
  }

  onSelectSuggestion = (suggestion: TypeaheadMultiSelect.Suggestion) => {
    const { selections } = this.props;
    if (suggestion.exclusive) {
      if (!!selections[suggestion.value]) {
        this.setSelection(suggestion, false);
      } else {
        this.selectExclusiveSelection(suggestion);
      }
    } else {
      // check if exclusive selection is selected
      if (this.exclusiveSuggestionIsSelected()) {
        this.removeExclusiveSelection(suggestion);
      } else {
        this.toggleSelection(suggestion);
      }
    }
  };

  toggleSelection(suggestion: TypeaheadMultiSelect.Suggestion) {
    const { selections } = this.props;
    if (selections[suggestion.value]) {
      this.setSelection(suggestion, false);
    } else {
      this.setSelection(suggestion, true);
    }
  }

  removeExclusiveSelection(newSelection: TypeaheadMultiSelect.Suggestion) {
    const { onSelectionChange } = this.props;
    onSelectionChange({ [newSelection.value]: newSelection });
  }

  selectExclusiveSelection(exclusiveSuggestion: TypeaheadMultiSelect.Suggestion) {
    const { onSelectionChange } = this.props;
    onSelectionChange({ [exclusiveSuggestion.value]: exclusiveSuggestion });
  }

  setSelection(suggestion: TypeaheadMultiSelect.Suggestion, selected: boolean) {
    const { onSelectionChange, selections } = this.props;
    if (selected) {
      const newSelectionState = { ...selections, [suggestion.value]: suggestion };
      onSelectionChange(newSelectionState);
    } else {
      const newSelectionState = cloneDeep(selections);
      delete newSelectionState[suggestion.value];
      onSelectionChange(newSelectionState);
    }
  }

  toggleShowSuggestions() {
    const { suggestionsAreOpen } = this.state;
    if (suggestionsAreOpen) {
      this.closeSuggestions();
    } else {
      this.setState({ suggestionsAreOpen: true });
    }
  }

  getInputValue() {
    const { value, suggestionsAreOpen } = this.state;
    const { selections } = this.props;
    const { getPlaceholderText } = this.props;
    if (suggestionsAreOpen) {
      return value;
    }
    return getPlaceholderText(selections);
  }

  renderNoResultsFound() {
    const { id } = this.props;
    return (
      <div id={`${id}-noresults`} className={css(styles.suggestionWrapper, styles.noResults)}>
        <Text fontSize="md" colorName="NEUTRAL_700">
          No results
        </Text>
      </div>
    );
  }

  /**
   *
   * @returns id of the dropdown list item that is currently active
   */
  getActiveListItemId = () => {
    const { suggestions, activeSuggestionIndex } = this.state;
    const { id } = this.props;

    const elementId =
      activeSuggestionIndex > -1 ? getSafeId(`${id}-${suggestions[activeSuggestionIndex].value}-suggestion`) : '';

    return elementId;
  };

  renderInputComponent() {
    const { getPlaceholderText, id, selections, disabled } = this.props;
    const { suggestionsAreOpen } = this.state;
    return (
      <TextInput
        inputRef={node => (this.textInputNode = node)}
        id={id}
        data-testid={id}
        data-track-element-name="TypeaheadMultiselect-TextInput"
        type="text"
        configOverrides={{
          icon: {
            id: `${id}-dropdown-icon`,
            onClick: event => {
              event.stopPropagation();
              this.toggleShowSuggestions();
            },
            size: 16,
            position: 'right',
            type: 'arrow_drop_down',
          },
        }}
        placeholder={getPlaceholderText(selections)}
        onClick={() => this.setState({ suggestionsAreOpen: true })}
        onChange={this.onInputChange}
        value={this.getInputValue()}
        onFocus={() => this.setState({ suggestionsAreOpen: true })}
        autoComplete="off"
        role="combobox"
        aria-expanded={suggestionsAreOpen}
        aria-autocomplete="list"
        aria-controls="it-suggestions-list"
        aria-activedescendant={this.getActiveListItemId()}
        onKeyDown={this.handleKeyPress}
        disabled={disabled}
      />
    );
  }

  renderSuggestions() {
    const { suggestions, suggestionsAreOpen, activeSuggestionIndex } = this.state;
    const { id, renderFooter, ariaLabelledById, selections } = this.props;
    return (
      <div
        className={css(styles.suggestionsRoot, suggestionsAreOpen ? styles.showSuggestions : styles.hideSuggestions)}
        id={`${id}-SuggestionsContainer`}
      >
        <ul
          className={css(styles.suggestionsContainer)}
          data-it="suggestions-list"
          id="it-suggestions-list"
          role="listbox"
          aria-labelledby={ariaLabelledById}
          aria-multiselectable="true"
        >
          {isEmpty(suggestions)
            ? this.renderNoResultsFound()
            : map(suggestions, (suggestion, index) => {
                return (
                  <TypeaheadMultiSelectSuggestion
                    key={suggestion.value}
                    shouldKeyboardFocus={activeSuggestionIndex === index}
                    suggestionProps={{
                      key: suggestion.value,
                      index,
                      isLastSuggestion: index === suggestions.length - 1,
                    }}
                    suggestion={suggestion}
                    onSelectSuggestion={this.onSelectSuggestion}
                    typeaheadId={id}
                    isChecked={!!selections[suggestion.value]}
                  />
                );
              })}
          {renderFooter && renderFooter(this.state)}
        </ul>
      </div>
    );
  }

  render() {
    const { id, selections, showDismissableTiles } = this.props;
    return (
      <div ref={node => (this.rootNode = node)}>
        <DomEventListener target={window.document} eventName="click" cb={this.closeOnOutsideClick} />
        <DomEventListener target={window.document} eventName="keydown" cb={this.maybeCloseOnKeyPress} />
        {this.renderInputComponent()}
        {this.renderSuggestions()}
        {showDismissableTiles && (
          <DismissableTileList
            id={id}
            selections={selections}
            onSelectionDeletion={selection => this.setSelection(selection, false)}
          />
        )}
      </div>
    );
  }
}
